import { centsDelta } from "./format";
import { type DeskMarket, MIN_BASELINE_USD } from "./markets";

// Why a market ranks as unusual, from the fields gamma gives us.
//
// A high volume multiple alone mostly finds the calendar: a match on match day trades 100×
// its quiet-week average, and so does an election on election night. So markets are first
// sorted into groups, and only those with no scheduled reason to be busy get a score:
//
//   past_end   end date has passed and the market is still open
//   scheduled  a fixture, or ends within 48h
//   new        under 7 days old, so there is no baseline to compare with
//   ranked     everything else
//
// score = (volume + peers + price) × discount, 0 to 100
//   volume    12 points per doubling over the market's own baseline
//   peers     takes off the same points for the category's median multiple, so a day when a
//             whole category is busy (the morning after an election) does not count.
//             Volume net of peers stops at 60
//   price     16 points per unit of log-odds moved in 24h beyond the category's median move,
//             up to 40. Log-odds because 3¢ to 8¢ is a bigger surprise than 50¢ to 55¢
//   discount  scales down markets that end within a week, or are under 3 weeks old

export type MarketGroup = "ranked" | "scheduled" | "new" | "past_end";

const HOUR_MS = 3_600_000;
const SOON_H = 48;
const WEEK_H = 168;
const NEW_DAYS = 7;
const YOUNG_DAYS = 21;
const MIN_PEERS = 8;

const VOLUME_PTS_PER_DOUBLING = 12;
const VOLUME_PTS_MAX = 60;
const PRICE_PTS_PER_LOGIT = 16;
const PRICE_PTS_MAX = 40;
const MIN_SOON_FACTOR = 0.5;
const MIN_YOUNG_FACTOR = 0.6;
const PRICE_CLAMP = 0.02;

export interface ScoreParts {
	score: number;
	volumePts: number;
	/** Zero or negative. */
	peerPts: number;
	pricePts: number;
	discount: number;
	discountWhy: string | null;
	/** Median volume multiple across the peer group. */
	peerVolX: number | null;
	peerLabel: string;
	peerCount: number;
	/** The baseline was under the floor, so the multiple is a lower bound. */
	thinBaseline: boolean;
}

export interface Ranked<T extends DeskMarket = DeskMarket> {
	market: T;
	group: MarketGroup;
	/** Why it is outside the ranking, in a few words. */
	note: string | null;
	parts: ScoreParts | null;
	/** Other ranked markets from the same event, which this row stands in for. */
	siblings: number;
	/** A higher-scoring market from the same event stands in for this one. */
	folded: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function median(values: number[]): number | null {
	if (values.length === 0) return null;
	const s = [...values].sort((a, b) => a - b);
	const mid = s.length >> 1;
	return s.length % 2 ? (s[mid] as number) : ((s[mid - 1] as number) + (s[mid] as number)) / 2;
}

const logit = (p: number) => {
	const c = clamp(p, PRICE_CLAMP, 1 - PRICE_CLAMP);
	return Math.log(c / (1 - c));
};

/** Size of the 24h move in log-odds, or null when the price or change is missing. */
export function logOddsMove(price: number | null | undefined, change: number | null | undefined): number | null {
	if (price === null || price === undefined || change === null || change === undefined) return null;
	return Math.abs(logit(price) - logit(price - change));
}

const hoursUntil = (iso: string | null, nowMs: number): number | null => {
	if (!iso) return null;
	const t = new Date(iso).getTime();
	return Number.isFinite(t) ? (t - nowMs) / HOUR_MS : null;
};

function spanText(hours: number): string {
	const h = Math.abs(hours);
	return h < 48 ? `${Math.max(1, Math.round(h))}h` : `${Math.round(h / 24)}d`;
}

const RESOLUTION_NOTE: Record<string, string> = {
	proposed: "result proposed",
	disputed: "result disputed",
	challenged: "result challenged",
};

/** Which group a market belongs to, and why (pure). */
export function classify(m: DeskMarket, nowMs: number): { group: MarketGroup; note: string | null } {
	const left = hoursUntil(m.endDate, nowMs);
	if (left !== null && left < 0) {
		const state = (m.resolution && RESOLUTION_NOTE[m.resolution]) ?? "no result yet";
		return { group: "past_end", note: `${spanText(left)} past end · ${state}` };
	}
	if (m.isGame) {
		if (m.gameStartMs === null) return { group: "scheduled", note: "fixture" };
		const toGame = (m.gameStartMs - nowMs) / HOUR_MS;
		return { group: "scheduled", note: toGame >= 0 ? `fixture in ${spanText(toGame)}` : "fixture started" };
	}
	if (left !== null && left < SOON_H) return { group: "scheduled", note: `ends in ${spanText(left)}` };
	if (m.ageDays === null) return { group: "new", note: "no start date" };
	if (m.ageDays < NEW_DAYS) return { group: "new", note: `${Math.floor(m.ageDays)}d old` };
	return { group: "ranked", note: null };
}

function discountFor(m: DeskMarket, nowMs: number): { discount: number; why: string | null } {
	const left = hoursUntil(m.endDate, nowMs);
	const soon = left !== null && left < WEEK_H
		? MIN_SOON_FACTOR + ((1 - MIN_SOON_FACTOR) * (left - SOON_H)) / (WEEK_H - SOON_H)
		: 1;
	const age = m.ageDays ?? YOUNG_DAYS;
	const young = age < YOUNG_DAYS
		? MIN_YOUNG_FACTOR + ((1 - MIN_YOUNG_FACTOR) * (age - NEW_DAYS)) / (YOUNG_DAYS - NEW_DAYS)
		: 1;
	const why = [
		soon < 1 && left !== null ? `ends in ${spanText(left)}` : null,
		young < 1 ? `only ${Math.floor(age)}d old` : null,
	].filter(Boolean);
	return { discount: clamp(soon, MIN_SOON_FACTOR, 1) * clamp(young, MIN_YOUNG_FACTOR, 1), why: why.join(", ") || null };
}

interface Peers {
	label: string;
	count: number;
	medianLog2: number;
	medianMove: number;
}

function peerGroups(eligible: DeskMarket[]): (m: DeskMarket) => Peers {
	const summarise = (list: DeskMarket[], label: string): Peers => ({
		label,
		count: list.length,
		medianLog2: median(list.flatMap((m) => (m.volX && m.volX > 0 ? [Math.log2(m.volX)] : []))) ?? 0,
		medianMove: median(list.flatMap((m) => {
			const move = logOddsMove(m.outcomes[0]?.price, m.change24);
			return move === null ? [] : [move];
		})) ?? 0,
	});
	const all = summarise(eligible, "all markets");
	const byCategory = new Map<string, DeskMarket[]>();
	for (const m of eligible) {
		if (!m.category) continue;
		const list = byCategory.get(m.category);
		if (list) list.push(m);
		else byCategory.set(m.category, [m]);
	}
	const groups = new Map<string, Peers>();
	for (const [name, list] of byCategory) {
		if (list.length >= MIN_PEERS) groups.set(name, summarise(list, name));
	}
	return (m) => (m.category && groups.get(m.category)) || all;
}

/** The score and its parts for one ranked market (pure). */
export function scoreMarket(m: DeskMarket, peers: Peers, nowMs: number): ScoreParts {
	const rawVolume = m.volX && m.volX > 1 ? VOLUME_PTS_PER_DOUBLING * Math.log2(m.volX) : 0;
	// Never more than the volume points: a quiet market in a busy category is quiet, not negative.
	const peerPts = -Math.min(rawVolume, VOLUME_PTS_PER_DOUBLING * Math.max(0, peers.medianLog2));
	// The cap is on what is left after peers, so 64× among 8× peers still earns its 3 doublings.
	const volumePts = Math.min(rawVolume, VOLUME_PTS_MAX - peerPts);
	const move = logOddsMove(m.outcomes[0]?.price, m.change24) ?? 0;
	const pricePts = clamp(PRICE_PTS_PER_LOGIT * (move - peers.medianMove), 0, PRICE_PTS_MAX);
	const { discount, why } = discountFor(m, nowMs);
	return {
		score: (volumePts + peerPts + pricePts) * discount,
		volumePts,
		peerPts,
		pricePts,
		discount,
		discountWhy: why,
		peerVolX: 2 ** peers.medianLog2,
		peerLabel: peers.label,
		peerCount: peers.count,
		thinBaseline: m.baselineDaily !== null && m.baselineDaily < MIN_BASELINE_USD,
	};
}

/**
 * Groups and scores `markets` (pure). Peers are measured only over ranked markets that are
 * not already decided, so a settled market or a fixture cannot move a category's median.
 * Within an event, the highest scorer stands in for the rest, which are marked `folded`:
 * thirty brackets of one question are one finding.
 */
export function rankMarkets<T extends DeskMarket>(markets: T[], nowMs: number): Ranked<T>[] {
	const grouped = markets.map((market) => ({ market, ...classify(market, nowMs) }));
	const eligible = grouped.filter((g) => g.group === "ranked" && !g.market.settled).map((g) => g.market);
	const peersOf = peerGroups(eligible);
	const rows: Ranked<T>[] = grouped.map((g) => ({
		...g,
		parts: g.group === "ranked" ? scoreMarket(g.market, peersOf(g.market), nowMs) : null,
		siblings: 0,
		folded: false,
	}));

	const best = new Map<string, Ranked<T>>();
	const foldable = (r: Ranked<T>) => Boolean(r.market.eventId && r.parts && !r.market.settled);
	for (const r of rows) {
		if (!foldable(r)) continue;
		const id = r.market.eventId as string;
		const top = best.get(id);
		if (!top?.parts || (r.parts?.score ?? 0) > top.parts.score) best.set(id, r);
	}
	for (const r of rows) {
		const top = foldable(r) ? best.get(r.market.eventId as string) : undefined;
		if (top && top !== r) {
			r.folded = true;
			top.siblings += 1;
		}
	}
	return rows;
}

/** A volume multiple for display. A floored baseline makes it a lower bound. */
export function multiple(x: number | null | undefined, atLeast: boolean): string {
	if (x === null || x === undefined || !Number.isFinite(x)) return "–";
	return `${atLeast ? "≥" : ""}${x < 10 ? x.toFixed(1) : x.toFixed(0)}×`;
}

/** The reason for a row in one plain line, for phones and the drawer (pure). */
export function whySentence(m: DeskMarket, parts: ScoreParts | null, note: string | null): string {
	if (!parts) return note ?? "";
	const lead = `${multiple(m.volX, parts.thinBaseline)} usual volume, ${parts.peerLabel} ${multiple(parts.peerVolX, false)}`;
	return m.change24 ? `${lead}, price ${centsDelta(m.change24)}` : lead;
}
